import React from "react";
import { useIntl } from "react-intl";

import type { TableData } from "../../utils/types";
import fetchDashboardData from "../../utils/fetch-dashboard-data";

import { filterChurnData } from "./metrics-ltv";
import { CHURN_SUMMARY, MAX_MONTHS_IN_PAST, MAX_QUARTERS_IN_PAST, TemporalResOptions, TempRes } from "./constants";
import type { ConsumersScreenProps } from "./components/consumers-screen";

function fetchChurnData(dashboardDataPath: string, monthly: boolean, setChurnRawData, setNeedsUpdate, setDataError) {
  fetchDashboardData(
    dashboardDataPath,
    monthly ? "customer_churn_monthly" : "customer_churn_quarterly",
    "TO_DATE",
    [],
    [],
    [],
    [],
    (data) => {
      setChurnRawData(data);
      setNeedsUpdate(true);
    },
    (error) => {
      setDataError(error);
    }
  );
}

export const useChurn = (
  dashboardDataPath: string,
  selectedTempResIndex: number,
  startDateString: string,
  endDateString: string,
  setDataError
): ConsumersScreenProps["churnTabProps"] => {
  const intl = useIntl();

  const [churnRawData, setChurnRawData] = React.useState<TableData>(null);
  const [churnPeriodInput, setChurnPeriodInput] = React.useState<string>("3");
  const [churnPeriod, setChurnPeriod] = React.useState<number>(3);

  const [needsUpdate, setNeedsUpdate] = React.useState<boolean>(true);
  const [newExisting, setNewExisting] = React.useState<boolean>(false);

  const [churnChartData, setChurnChartData] = React.useState<ConsumersScreenProps["churnTabProps"]["chartData"]>({
    syncId: "dummy-id",
    data: [],
    dataList: [],
  });
  const [interpretationLine, setInterpretationLine] = React.useState<string>("");

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    setNeedsUpdate(true);
  }, [startDateString, endDateString]);

  if (!churnRawData) {
    const tempRes = TemporalResOptions[selectedTempResIndex].id;
    const monthly = tempRes === TempRes.Month;
    fetchChurnData(dashboardDataPath, monthly, setChurnRawData, setNeedsUpdate, setDataError);
  }

  React.useEffect(() => {
    const tempRes = TemporalResOptions[selectedTempResIndex].id;
    const monthly = tempRes === TempRes.Month;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    setChurnPeriodInput(selectedTempResIndex === 0 ? "3" : "1");
    setChurnPeriod(selectedTempResIndex === 0 ? 3 : 1);

    fetchChurnData(dashboardDataPath, monthly, setChurnRawData, setNeedsUpdate, setDataError);
  }, [dashboardDataPath, selectedTempResIndex, setDataError]);

  const onChangeChurnPeriod = (event) => {
    // We only permit integers between 1 and 24/8 for months/quarters
    const tempRes = TemporalResOptions[selectedTempResIndex].id;
    const maxValue = tempRes === TempRes.Month ? MAX_MONTHS_IN_PAST : MAX_QUARTERS_IN_PAST;
    if (
      !isNaN(event.target.value) &&
      (!event.target.value ||
        (Number.isInteger(parseFloat(event.target.value)) &&
          parseInt(event.target.value) < maxValue &&
          parseInt(event.target.value) > 0))
    ) {
      setChurnPeriodInput(event.target.value);
      if (event.target.value) {
        setChurnPeriod(parseInt(event.target.value));
        setNeedsUpdate(true);
      }
    }
  };

  const tempRes = TemporalResOptions[selectedTempResIndex].id;
  const churnPeriodUnit =
    tempRes === TempRes.Month
      ? intl.formatMessage({ id: "Churn: Months Unit", defaultMessage: "months" })
      : intl.formatMessage({ id: "Churn: Quarters Unit", defaultMessage: "quarters" });
  const maxChurn = tempRes === TempRes.Month ? MAX_MONTHS_IN_PAST : MAX_QUARTERS_IN_PAST;

  if (needsUpdate && churnRawData && startDateString && endDateString) {
    const data = churnRawData["data"][churnPeriod.toString()];

    const percentLines = [];
    const numberLines = [];

    const churnData = filterChurnData(intl, data, tempRes, startDateString, endDateString);

    if (churnData.length > 0) {
      const dataPoint = churnData[churnData.length > 1 ? 1 : 0];
      const churnPeriodUnitString =
        churnPeriod > 1 ? churnPeriodUnit : churnPeriodUnit.slice(0, churnPeriodUnit.length - 1);

      setInterpretationLine(
        `In ${dataPoint.day} you had ${dataPoint.totalCurrent} orders. Of these ${dataPoint.churnedCurrent} (${dataPoint.percent_churnCurrent}%) didn't return within ${churnPeriod} ${churnPeriodUnitString}.`
      );

      if (newExisting) {
        percentLines.push({ datumName: "percent_retain", name: "Returned Customers" });
        percentLines.push({ datumName: "percent_new_churn", name: "Churned New Customers" });
        percentLines.push({ datumName: "percent_existing_churn", name: "Churned Existing Customers" });

        numberLines.push({ datumName: "total", name: "Total Customers" });
        numberLines.push({ datumName: "newChurned", name: "Churned New Customers" });
        numberLines.push({ datumName: "existingChurned", name: "Churned Existing Customers" });
      } else {
        percentLines.push({ datumName: "percent_retain", name: "Returned Customers" });
        percentLines.push({ datumName: "percent_churn", name: "Churned Customers" });
        numberLines.push({ datumName: "total", name: "Total Customers" });
        numberLines.push({ datumName: "churned", name: "Churned Customers" });
      }
    }
    setChurnChartData({
      syncId: "dummy-id",
      data: churnData,
      dataList: [
        {
          formatValue: (s: number | string | readonly (number | string)[]) => {
            const numeric = Number(s);
            return Number.isFinite(numeric) && numeric > 0 ? `${numeric}%` : "";
          },
          chartTitle: "% of Customers who Churned",
          lines: percentLines,
        },
        {
          formatValue: (s) => +s,
          chartTitle: "Number of Customers",
          lines: numberLines,
          // stackedBars: false, //TODO expose this in flyui again if we're going to release this screen
        },
      ],
      showLegend: true,
    });
    setNeedsUpdate(false);
  }

  return {
    title: "Churn",
    chartData: churnChartData,
    onChangeChurnPeriod,
    churnPeriod: churnPeriodInput,
    churnPeriodTooltip:
      intl.formatMessage({
        id: "Churn: Churn period tooltip",
        defaultMessage: `Churn period is how long before customers are considered to have abandoned. Enter a number between 1 and `,
      }) + (maxChurn - 1).toString(),
    churnPeriodUnit: churnPeriodUnit,
    toggleProps: {
      label: "New/Existing",
      on: newExisting,
      onChange: (on: boolean) => {
        setNeedsUpdate(true);
        setNewExisting(on);
      },
      tooltip: "Split customers into new vs. existing customers",
    },
    summaryText: CHURN_SUMMARY + interpretationLine,
  };
};
